/**
 * SigV4-signed JWKS resolver. Pulls a private `jwks.json` from S3 via
 * the AWS SDK (NOT anonymous HTTPS) and exposes a `jose`-compatible
 * `GetKeyFn`.
 *
 * Lifted 1:1 from provisioning's `server/lib/auth/sigv4-s3-jwks.ts`.
 */
import {
  S3Client,
  GetObjectCommand,
  type GetObjectCommandOutput,
} from "@aws-sdk/client-s3";
import {
  createLocalJWKSet,
  errors,
  type FlattenedJWSInput,
  type JWK,
  type JWSHeaderParameters,
} from "jose";

type JWKSet = { keys: JWK[] };

/**
 * jose-compatible dynamic-key resolver. Matches the shape `jwtVerify`
 * accepts (`createLocalJWKSet`'s return type) — the resolver returns the
 * WebCrypto `CryptoKey` jose hands to the verifier.
 */
export type GetKeyFn = (
  protectedHeader: JWSHeaderParameters,
  token?: FlattenedJWSInput,
) => Promise<CryptoKey>;

export type SigV4S3RemoteJWKSetOptions = {
  region: string;
  bucket: string;
  key: string; // ".well-known/jwks.json"

  cacheMaxAgeMs?: number;
  cooldownDurationMs?: number;
  s3Client?: S3Client;

  /**
   * Explicit S3 endpoint override. When set — or when the standard
   * `AWS_ENDPOINT_URL_S3` (service-specific) or `AWS_ENDPOINT_URL` (global) env
   * var is set — the S3 client targets this endpoint with path-style addressing,
   * required to reach LocalStack / MinIO / any non-AWS or path-style S3 (req
   * a50c8d74; global-var fallback per drift b02d1d9f). Precedence mirrors the AWS
   * SDK: this option > `AWS_ENDPOINT_URL_S3` > `AWS_ENDPOINT_URL`.
   *
   * When NONE of those is set, the client uses the AWS-prod default (no
   * endpoint, virtual-host addressing) — standard AWS deploys are UNCHANGED.
   */
  s3Endpoint?: string;

  /**
   * Force path-style S3 addressing (`https://endpoint/bucket/key` instead
   * of `https://bucket.endpoint/key`). Defaults to `true` whenever an
   * endpoint is resolved (LocalStack/MinIO require it). Set to `false` to
   * opt out even with a custom endpoint. Ignored on the AWS-prod default
   * path (no endpoint).
   */
  forcePathStyle?: boolean;
};

async function streamToString(
  body: GetObjectCommandOutput["Body"],
): Promise<string> {
  if (!body) return "";

  // Bun + web streams: the SDK Body union includes a `ReadableStream`,
  // which exposes `getReader`. The union members are duck-typed at
  // runtime, so narrow against the methods we actually call.
  const webStream = body as Partial<ReadableStream<Uint8Array>>;
  if (typeof webStream.getReader === "function") {
    const reader = webStream.getReader();
    const decoder = new TextDecoder();
    let out = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      out += decoder.decode(value, { stream: true });
    }
    return out;
  }

  // Fallback: the SDK mixes a `transformToString` helper onto the body in
  // some builds (returns a Promise). Narrow explicitly against it.
  const sdkStream = body as {
    transformToString?: (enc?: string) => Promise<string>;
  };
  if (typeof sdkStream.transformToString === "function") {
    return sdkStream.transformToString("utf8");
  }

  throw new Error("Unsupported S3 Body stream type (Bun)");
}

/**
 * Build the S3 client the resolver uses, honoring the endpoint override
 * (option > `AWS_ENDPOINT_URL_S3` env) + path-style addressing (req a50c8d74).
 *
 * On the AWS-prod default path (neither option nor env set) the client is
 * constructed with NO `endpoint` and NO `forcePathStyle` — virtual-host
 * addressing, unchanged from upstream (back-compat). Exported for tests so the
 * prod-default branch is asserted on the ACTUAL factory logic (not a hand-rolled
 * client). Mirrors the Go `buildSigV4S3Client` seam.
 */
export function buildSigV4S3Client(
  opts: Pick<
    SigV4S3RemoteJWKSetOptions,
    "region" | "s3Endpoint" | "forcePathStyle"
  >,
): S3Client {
  // Endpoint precedence mirrors the AWS SDK's own resolution (drift b02d1d9f):
  // explicit option > service-specific `AWS_ENDPOINT_URL_S3` > GLOBAL
  // `AWS_ENDPOINT_URL`. A full local stack (F2) sets only the GLOBAL var, so
  // honoring it lets the SigV4-fronted JWKS fetch reach LocalStack/MinIO (it
  // previously read only the S3-specific var → S2S verify couldn't bootstrap
  // locally). Prod is unaffected (no endpoint env set → virtual-host default).
  const endpoint =
    opts.s3Endpoint ??
    process.env.AWS_ENDPOINT_URL_S3 ??
    process.env.AWS_ENDPOINT_URL;
  // Path-style is required by LocalStack/MinIO; default it on whenever an
  // endpoint is resolved, but let a caller explicitly opt out.
  const forcePathStyle = opts.forcePathStyle ?? true;
  return new S3Client({
    region: opts.region,
    ...(endpoint ? { endpoint, forcePathStyle } : {}),
  });
}

export function createSigV4S3JWKSet(
  opts: SigV4S3RemoteJWKSetOptions,
): GetKeyFn {
  // Honor an explicit S3 endpoint (option > AWS_ENDPOINT_URL_S3 env) so the
  // resolver can reach LocalStack / MinIO / path-style S3 (req a50c8d74).
  // When neither is set, the AWS-prod default (no endpoint, virtual-host
  // addressing) is preserved — standard deploys are unaffected.
  const s3 = opts.s3Client ?? buildSigV4S3Client(opts);

  const cacheMaxAgeMs = opts.cacheMaxAgeMs ?? 5 * 60_000;
  const cooldownDurationMs = opts.cooldownDurationMs ?? 10_000;

  let local: ReturnType<typeof createLocalJWKSet> | null = null;
  let lastFetchedAt = 0;
  let lastAttemptAt = 0;
  let inflight: Promise<void> | null = null;

  async function fetchAndBuild(): Promise<void> {
    const now = Date.now();
    if (now - lastAttemptAt < cooldownDurationMs) return;
    lastAttemptAt = now;

    const res = await s3.send(
      new GetObjectCommand({
        Bucket: opts.bucket,
        Key: opts.key,
      }),
    );

    const text = await streamToString(res.Body);

    let jwks: JWKSet;
    try {
      jwks = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JWKS JSON from s3://${opts.bucket}/${opts.key}`);
    }

    if (!Array.isArray(jwks.keys)) {
      throw new Error("JWKS must contain a 'keys' array");
    }

    local = createLocalJWKSet(jwks);
    lastFetchedAt = Date.now();
  }

  // ensureFresh refreshes the cache when missing/stale (or forced). It RETURNS
  // the refresh error rather than throwing it, so the caller can decide whether
  // a stale-but-usable `local` set can still serve the kid (graceful
  // degradation per spec § 5.6 — parity with the Go `jwks.go` + Python
  // `jwks.py` resolvers, which serve a known kid from stale cache when a
  // refresh fails instead of rejecting on the first failure).
  async function ensureFresh(force = false): Promise<Error | null> {
    const now = Date.now();
    const stale = now - lastFetchedAt > cacheMaxAgeMs;

    if (!force && local && !stale) return null;

    if (!inflight) {
      inflight = (async () => {
        try {
          await fetchAndBuild();
        } finally {
          inflight = null;
        }
      })();
    }

    try {
      await inflight;
      return null;
    } catch (err) {
      return err instanceof Error ? err : new Error(String(err));
    }
  }

  return async (protectedHeader, token) => {
    // Refresh if missing/stale. A FAILED refresh does NOT immediately reject —
    // if we already hold a (possibly stale) JWKS set, try to serve the kid
    // from it first (serve-stale-on-failed-refresh, § 5.6 graceful
    // degradation; Go/Python parity).
    const refreshErr = await ensureFresh(false);

    if (!local) {
      // No usable cache at all → we MUST have a fresh set; a refresh failure
      // here is terminal (nothing stale to fall back to).
      if (refreshErr) throw refreshErr;
      // The first ensureFresh may have no-op'd (cooldown window) leaving the
      // cache empty — force one refresh and SURFACE its error rather than
      // dropping it (otherwise `local` stays null and the resolve below would
      // return `undefined` instead of rejecting cleanly).
      const forcedErr = await ensureFresh(true);
      if (forcedErr) throw forcedErr;
      // Cold cache + a refresh that "succeeded" but produced no set (e.g.
      // cooldown no-op) → reject cleanly instead of returning undefined.
      if (!local) {
        throw new Error(
          `Unable to load JWKS from s3://${opts.bucket}/${opts.key} (cache empty after refresh)`,
        );
      }
    }

    try {
      return (await local?.(protectedHeader, token)) as CryptoKey;
    } catch (err) {
      const code =
        typeof err === "object" && err !== null && "code" in err
          ? (err as { code?: unknown }).code
          : undefined;
      if (
        err instanceof errors.JWKSNoMatchingKey ||
        code === "ERR_JWKS_NO_MATCHING_KEY"
      ) {
        // kid not in the current set → force one refresh (rotation overlap
        // window, § 5.6). If THAT refresh fails, fall through to re-resolve
        // against the stale set: a known kid still served, an unknown kid
        // still rejected as ERR_JWKS_NO_MATCHING_KEY.
        await ensureFresh(true);
        return (await local?.(protectedHeader, token)) as CryptoKey;
      }
      throw err;
    }
  };
}
